// Dr.Rx™ Primary Application Shell & Router
import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import Chat from './components/Chat';
import Footer from './components/Footer';
import Dashboard from './components/Dashboard';
import Tools from './components/Tools';
import Account from './components/Account';
import AllTools from './components/AllTools';
import EvidenceSearch from './components/EvidenceSearch';
import DrugIndex from './components/DrugIndex';
import DrugDetail from './components/DrugDetail';
import StudyMode from './components/StudyMode';
import Quiz from './components/Quiz';
import ClinicalGuides from './components/ClinicalGuides';
import AddDrugModal from './components/AddDrugModal';
import InstallInstructionsModal from './components/InstallInstructionsModal';
import Pricing from './components/Pricing';
import UpgradeModal from './components/UpgradeModal';
import Login from './components/Login';
import SubscriptionManagement from './components/SubscriptionManagement';
import AdminDashboard from './components/AdminDashboard'; 
import { findDrugByName, getDrugById, getUserDrugs } from './services/pharmacyService';
import { streamAnswerFromGemini, generateChatTitle } from './services/geminiService';
import { usePWAInstall } from './hooks/usePWAInstall';
import { useAuth } from './contexts/AuthContext';
import { useToast } from './contexts/ToastContext';
import type { View, QuizSettings, ChatSession, ChatSettings, Message, DrugInfo, AnswerLength, Activity } from './types';
import { ChatMessageRole } from './types';
import { useIdleTimer } from './hooks/useIdleTimer';
import InactivityModal from './components/InactivityModal';
import EditQuickAccessModal from './components/EditQuickAccessModal';
import ToolRenderer from './components/ToolRenderer';
import InfoModal from './components/InfoModal';
import OnboardingTour from './components/OnboardingTour';
import { infoContent } from './data/infoContent';
import { syncClinicianDataFromCloud, pushClinicianDataToCloud } from './services/userSyncService';


declare var Sentry: any;

const AnimatedLogo: React.FC = () => (
    <div className="relative w-24 h-24 mx-auto logo-float">
        <div className="logo-glow w-full h-full">
            <svg width="100%" height="100%" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g className="logo-arm-top">
                    <line x1="24" y1="9" x2="24" y2="17" stroke="white" strokeWidth="1.5"/>
                    <circle cx="24" cy="7" r="2" fill="white"/>
                </g>
                <g className="logo-arm-bottom">
                    <line x1="24" y1="31" x2="24" y2="39" stroke="white" strokeWidth="1.5"/>
                    <circle cx="24" cy="41" r="2" fill="white"/>
                </g>
                <g className="logo-arm-left">
                    <line x1="9" y1="24" x2="17" y2="24" stroke="white" strokeWidth="1.5"/>
                    <circle cx="7" cy="24" r="2" fill="white"/>
                </g>
                <g className="logo-arm-right">
                    <line x1="31" y1="24" x2="39" y2="24" stroke="white" strokeWidth="1.5"/>
                    <circle cx="41" cy="24" r="2" fill="white"/>
                </g>
                <circle cx="24" cy="24" r="5" fill="#FACC15" className="logo-center"/>
            </svg>
        </div>
    </div>
);


const App: React.FC = () => {
  const { user, uid, subscription, shouldShowPricing, decrementInteractions, setIsUpgradeModalOpen, isUpgradeModalOpen, logout, loading, loginWithGoogle, isAdmin } = useAuth();
  const { showToast, confirmAction } = useToast();
  
  const [currentView, setCurrentView] = useState<View>('dashboard');
  const [initialMessage, setInitialMessage] = useState<string | null>(null);
  const [initialAnswerLength, setInitialAnswerLength] = useState<AnswerLength>('short');
  const [initialQuery, setInitialQuery] = useState<string | null>(null);
  const [selectedDrugId, setSelectedDrugId] = useState<string | null>(null);
  const [quizSettings, setQuizSettings] = useState<QuizSettings | null>(null);

  const { isInstallable, isInstalled, promptInstall, platform, showIosInstallModal, setShowIosInstallModal } = usePWAInstall();

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [settings, setSettings] = useState<ChatSettings>({ professionalFocus: 'Pharmacist', llmModel: 'gemini-3.8-flash', answerLength: 'short' });
  const [isLoading, setIsLoading] = useState(false);
  const [drugToAdd, setDrugToAdd] = useState<string | null>(null);
  const sentInitialMessage = useRef(false);
  const stopStreamRef = useRef(false);

  const [showTour, setShowTour] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return !localStorage.getItem('drrx_onboarding_completed');
  });

  const handleCompleteTour = () => {
    localStorage.setItem('drrx_onboarding_completed', 'true');
    setShowTour(false);
  };

  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  const [isPushEnabled, setIsPushEnabled] = useState(false);
  const [isSentryEnabled, setIsSentryEnabled] = useState(true);

  const [quickAccessTools, setQuickAccessTools] = useState<string[]>([]);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [isEditQuickAccessOpen, setIsEditQuickAccessOpen] = useState(false);
  
  const [infoModalContent, setInfoModalContent] = useState<{ title: string; content: React.ReactNode } | null>(null);
  
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const { isIdle, countdown, resetTimer } = useIdleTimer({
    onIdle: logout,
    isPwaInstalled: isInstalled,
    countdownTimeInSeconds: 60, // 1 minute
  });
  
  const handleOpenInfoModal = (key: keyof typeof infoContent) => {
    setInfoModalContent(infoContent[key]);
  };

  // --- LOGIN REDIRECT LOGIC ---
  // Fix: Ensure we redirect to dashboard on fresh login, clearing old params
  useEffect(() => {
    if (user && uid) {
        // If we just logged in (loading finished), check if we should reset view
        // We only respect 'view' param if it was set intentionally during this session
        // For fresh logins, we default to dashboard to avoid "stuck on account page" loop.
        const params = new URLSearchParams(window.location.search);
        const viewParam = params.get('view') as View;
        
        // If coming from a cold load with no view param, or if we just authenticated:
        if (!viewParam) {
             setCurrentView('dashboard');
        }
    }
  }, [user, uid]);

  // --- SEO & DEEP LINKING LOGIC ---
  useEffect(() => {
    // Handle initial URL load
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view') as View;
    const id = params.get('id');
    
    if (view) setCurrentView(view);
    if (id) setSelectedDrugId(id);
    
    // Handle browser back/forward navigation
    const handlePopState = () => {
        const p = new URLSearchParams(window.location.search);
        const v = p.get('view') as View || 'dashboard';
        const i = p.get('id');
        setCurrentView(v);
        if (i) setSelectedDrugId(i);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Sync State changes back to URL
  useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const currentUrlView = params.get('view') || 'dashboard';
      const currentUrlId = params.get('id');
      
      // Only push history if the state is different from the URL to avoid loops
      if (currentUrlView !== currentView || (selectedDrugId && currentUrlId !== selectedDrugId)) {
          try {
              const newUrl = new URL(window.location.href);
              if (currentView === 'dashboard') {
                   newUrl.search = ''; // Clean URL for dashboard
              } else {
                   newUrl.searchParams.set('view', currentView);
                   if (currentView === 'drug-detail' && selectedDrugId) {
                       newUrl.searchParams.set('id', selectedDrugId);
                   } else {
                       newUrl.searchParams.delete('id');
                   }
              }
              window.history.pushState({}, '', newUrl.toString());
          } catch (e) {
              // In sandboxed environments, pushState may be blocked.
              console.warn("Navigation URL update suppressed in this environment.");
          }
      }
  }, [currentView, selectedDrugId]);

  // Effect to handle dynamic viewport height for iOS
  useEffect(() => {
    const setAppHeight = () => {
        const doc = document.documentElement;
        doc.style.setProperty('--app-height', `${window.innerHeight}px`);
    };
    window.addEventListener('resize', setAppHeight);
    setAppHeight(); 

    return () => window.removeEventListener('resize', setAppHeight);
  }, []);

  // Effect for Sentry user identification and context tagging
  useEffect(() => {
    if (isSentryEnabled && typeof Sentry !== 'undefined') {
      if (user && uid) {
        Sentry.setUser({ id: uid });
        Sentry.setTag("subscription_plan", subscription?.plan || "Unknown");
      } else {
        Sentry.setUser(null);
        Sentry.setTag("subscription_plan", "Unauthenticated");
      }
      Sentry.setTag("current_view", currentView);
    }
  }, [user, uid, isSentryEnabled, subscription, currentView]);

  useEffect(() => {
    if (shouldShowPricing) {
        setCurrentView('pricing');
    }
  }, [shouldShowPricing]);

  useEffect(() => {
      if (!user || !uid) return;
      try {
          const savedSessions = localStorage.getItem(`chatSessions_${uid}`);
          const savedSettings = localStorage.getItem(`chatSettings_${uid}`);
          const savedVoice = localStorage.getItem(`isVoiceEnabled_${uid}`);
          const savedPush = localStorage.getItem(`isPushEnabled_${uid}`);
          const savedQuickAccess = localStorage.getItem(`drrx_quickAccessTools_v2_${uid}`);
          const savedActivities = localStorage.getItem(`drrx_recentActivities_${uid}`);
          
          if (savedSessions) setSessions(JSON.parse(savedSessions));
          if (savedSettings) setSettings(prev => ({ ...prev, ...JSON.parse(savedSettings) }));
          if (savedVoice) setIsVoiceEnabled(JSON.parse(savedVoice));
          if (savedPush) setIsPushEnabled(JSON.parse(savedPush));
          if (savedActivities) setRecentActivities(JSON.parse(savedActivities));

          const pendingSentryPrefJSON = localStorage.getItem('drrx_pending_sentry_preference');
          const savedSentryJSON = localStorage.getItem(`drrx_sentry_reporting_enabled_${uid}`);
          let finalSentryPref = true; 

          if (savedSentryJSON !== null) {
              finalSentryPref = JSON.parse(savedSentryJSON);
          } else if (pendingSentryPrefJSON !== null) {
              finalSentryPref = JSON.parse(pendingSentryPrefJSON);
              localStorage.setItem(`drrx_sentry_reporting_enabled_${uid}`, JSON.stringify(finalSentryPref));
          } else {
              localStorage.setItem(`drrx_sentry_reporting_enabled_${uid}`, 'true');
          }
          setIsSentryEnabled(finalSentryPref);

          if (pendingSentryPrefJSON !== null) {
              localStorage.removeItem('drrx_pending_sentry_preference');
          }
          
          if (savedQuickAccess) {
              setQuickAccessTools(JSON.parse(savedQuickAccess));
          } else {
              setQuickAccessTools(['Interactions', 'Calculators', 'Dose Adjust', 'Cannabinoids']);
          }

          // Trigger asynchronous multi-device cloud synchronization
          syncClinicianDataFromCloud(uid).then((cloudData) => {
              if (cloudData.quickAccessTools && cloudData.quickAccessTools.length > 0) {
                  setQuickAccessTools(cloudData.quickAccessTools);
              }
              if (cloudData.recentActivities && cloudData.recentActivities.length > 0) {
                  setRecentActivities(cloudData.recentActivities);
              }
          }).catch(err => console.warn("Cloud sync deferred:", err));
      } catch (error) { console.error("Failed to load data from localStorage", error); }
  }, [user, uid]);

  useEffect(() => {
      if (!user || !uid) return;
      try { localStorage.setItem(`chatSessions_${uid}`, JSON.stringify(sessions)); } 
      catch (error) { console.error("Failed to save sessions to localStorage", error); }
  }, [sessions, user, uid]);

  useEffect(() => {
      if (!user || !uid) return;
      try { localStorage.setItem(`chatSettings_${uid}`, JSON.stringify(settings)); } 
      catch (error) { console.error("Failed to save settings to localStorage", error); }
  }, [settings, user, uid]);

  useEffect(() => {
      if (!user || !uid) return;
      try { localStorage.setItem(`isVoiceEnabled_${uid}`, JSON.stringify(isVoiceEnabled)); }
      catch (error) { console.error("Failed to save voice setting to localStorage", error); }
  }, [isVoiceEnabled, user, uid]);

  useEffect(() => {
    if (!user || !uid) return;
    try { localStorage.setItem(`isPushEnabled_${uid}`, JSON.stringify(isPushEnabled)); }
    catch (error) { console.error("Failed to save push notification setting to localStorage", error); }
  }, [isPushEnabled, user, uid]);
  
  useEffect(() => {
    if (!user || !uid) return;
    try { localStorage.setItem(`drrx_sentry_reporting_enabled_${uid}`, JSON.stringify(isSentryEnabled)); }
    catch (error) { console.error("Failed to save Sentry preference to localStorage", error); }
  }, [isSentryEnabled, user, uid]);

  useEffect(() => {
    if (!user || !uid) return;
    try { 
      localStorage.setItem(`drrx_quickAccessTools_v2_${uid}`, JSON.stringify(quickAccessTools)); 
      pushClinicianDataToCloud(uid, { quickAccessTools });
    }
    catch (error) { console.error("Failed to save quick access tools to localStorage", error); }
  }, [quickAccessTools, user, uid]);
  
  useEffect(() => {
    if (!user || !uid) return;
    try {
      localStorage.setItem(`drrx_recentActivities_${uid}`, JSON.stringify(recentActivities));
      pushClinicianDataToCloud(uid, { recentActivities });
    } catch (error) {
      console.error("Failed to save recent activities to localStorage", error);
    }
  }, [recentActivities, user, uid]);

  // Sentry Initialization Effect
  useEffect(() => {
    if (isSentryEnabled && typeof Sentry !== 'undefined') {
      const existingClient = typeof Sentry.getClient === 'function'
        ? Sentry.getClient()
        : (typeof Sentry.getCurrentHub === 'function' && Sentry.getCurrentHub().getClient());

      if (!existingClient) {
        const isLocalOrDev = typeof window !== 'undefined' && (
          window.location.hostname === 'localhost' ||
          window.location.hostname.includes('ais-dev') ||
          window.location.hostname.includes('127.0.0.1')
        );

        Sentry.init({
          dsn: "https://5bc74dd359b9d073d94642ab0f8d94bc@o4509973729443840.ingest.us.sentry.io/4510331205517312",
          environment: isLocalOrDev ? 'development' : 'production',
          sendDefaultPii: false,
          integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
          ],
          tracesSampleRate: 1.0,
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
          beforeSend(event: any, hint: any) {
            const error = hint?.originalException;
            if (error) {
              const msg = typeof error === 'string' ? error : (error.message || '');
              // Ignore benign browser/dev noise
              if (
                msg.includes('ResizeObserver loop') ||
                msg.includes('useAuth must be used within an AuthProvider') ||
                msg.includes('Failed to fetch') && isLocalOrDev
              ) {
                return null;
              }
            }
            return event;
          },
        });
        console.log(`[Sentry] Initialized successfully in ${isLocalOrDev ? 'development' : 'production'} mode.`);
      }
    }
  }, [isSentryEnabled]);


  useEffect(() => {
    if (initialMessage && currentView === 'chat' && !sentInitialMessage.current) {
        sentInitialMessage.current = true;
        handleSendMessage(initialMessage, initialAnswerLength);
    }
  }, [initialMessage, currentView, initialAnswerLength]);

  const addActivity = (activity: Omit<Activity, 'id' | 'timestamp'>) => {
      setRecentActivities(prev => {
          const newActivity: Activity = {
              ...activity,
              id: Date.now().toString(),
              timestamp: Date.now(),
          };
          if (prev[0]?.text === newActivity.text) return prev;
          return [newActivity, ...prev].slice(0, 10);
      });
  };
  
  const handleClearActivity = async () => {
    const confirmed = await confirmAction({
        title: 'Clear Recent Activity',
        message: 'Clear your recent clinical activity history from this device?',
        confirmLabel: 'Clear Activity',
        variant: 'warning'
    });
    if (confirmed) {
        setRecentActivities([]);
        if (user && uid) {
            localStorage.removeItem(`drrx_recentActivities_${uid}`);
        }
        showToast('Recent activity cleared.', 'info', 2000);
    }
  };

  const handleStopGenerating = () => {
    stopStreamRef.current = true;
    setIsLoading(false);
  };

  const handleSendMessage = async (message: string, answerLength: AnswerLength = 'short') => {
      if (!message.trim() || isLoading || !uid) return;
      if (navigator.vibrate) navigator.vibrate(10);
      
      if ((subscription?.plan === 'Trial' || subscription?.plan === 'Free') && subscription.interactionsLeft <= 0) {
        setIsUpgradeModalOpen(true);
        return;
      }

      const userMessage: Message = { id: Date.now().toString(), text: message, role: ChatMessageRole.USER };
      setIsLoading(true);
      stopStreamRef.current = false;
      
      const localData: DrugInfo | null = await findDrugByName(message, uid);

      let currentSessionId = activeSessionId;
      let currentHistory: Message[] = [];

      if (!currentSessionId) {
          currentSessionId = `session-${Date.now()}`;
          const newSession: ChatSession = { id: currentSessionId, title: "New Chat", messages: [], createdAt: Date.now() };
          setSessions(prev => [newSession, ...prev]);
          setActiveSessionId(currentSessionId);
          addActivity({
              type: 'start_chat',
              text: `Started chat: "${message.substring(0, 30)}..."`,
              payload: { sessionId: currentSessionId, query: message }
          });
          generateChatTitle(message).then(title => {
              setSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, title } : s));
          });
      } else {
        const existingSession = sessions.find(s => s.id === currentSessionId);
        if (existingSession) {
            currentHistory = existingSession.messages;
        }
      }

      const modelMessageId = (Date.now() + 1).toString();
      const initialModelMessage: Message = { 
        id: modelMessageId, 
        text: '', 
        role: ChatMessageRole.MODEL, 
        source: localData ? 'local' : 'gemini',
        blackBoxWarning: localData?.hasBlackBoxWarning ? (localData.blackBoxWarning || null) : null,
        matchedLocalDrugId: localData?.id,
        matchedLocalDrugName: localData?.brandName
      };
      
      setSessions(prev => prev.map(s => {
          if (s.id === currentSessionId) {
              return { ...s, messages: [...s.messages, userMessage, initialModelMessage] };
          }
          return s;
      }));

      const historyForContext = [...currentHistory, userMessage];

      try {
          const { stream, source } = await streamAnswerFromGemini(
              message, 
              historyForContext, 
              localData, 
              answerLength, 
              settings.professionalFocus,
              settings.llmModel
          );

          let fullResponseText = '';
          let groundingMetadata: any = null;
          for await (const chunk of stream) {
              if (stopStreamRef.current) break;
              const chunkText = (chunk as any).text;
              if (chunkText) {
                  fullResponseText += chunkText;
              }
              if ((chunk as any).candidates?.[0]?.groundingMetadata) {
                groundingMetadata = (chunk as any).candidates[0].groundingMetadata;
              }
              
               setSessions(prev => prev.map(s => {
                  if (s.id === currentSessionId) {
                      const newMessages = s.messages.map(m => 
                          m.id === modelMessageId ? { ...m, text: fullResponseText, source, groundingMetadata } : m
                      );
                      return { ...s, messages: newMessages };
                  }
                  return s;
              }));
          }
           
          if (stopStreamRef.current) {
               setIsLoading(false);
               return;
          }

          let finalText = fullResponseText;

          const addDrugMatch = finalText.match(/DRR_ADD_DRUG:\s*\[?([^\]\n]+)\]?/i);
          const toolRegex = /DRR_RECOMMEND_TOOL:\s*\[?([^\]\n]+)\]?/gi;
          const toolMatches = [...finalText.matchAll(toolRegex)];
          
          let toolNames: string[] = [];
          toolMatches.forEach(m => {
              if (m[1]) {
                  const splitTools = m[1].split(',').map(t => t.trim()).filter(Boolean);
                  toolNames = [...toolNames, ...splitTools];
              }
          });

          // Clean tags from visible text
          finalText = finalText.replace(toolRegex, '').trim();

          const followUpMatch = finalText.match(/DRR_FOLLOW_UP:\s*(\[.*?\])/s);

          let action: Message['action'] | undefined = undefined;
          let recommendedTools: string[] | undefined = toolNames.length > 0 ? Array.from(new Set(toolNames)) : undefined;
          let addDrugName: string | undefined = undefined;
          let followUpPrompts: string[] | undefined = undefined;
          
          if (addDrugMatch?.[1]) {
              const extractedDrug = addDrugMatch[1].trim();
              if (subscription?.plan !== 'Free' && subscription?.plan !== 'Trial') {
                  addDrugName = extractedDrug;
                  action = { type: 'ADD_DRUG', drugName: extractedDrug };
              }
              finalText = finalText.replace(addDrugMatch[0], '').trim();
          }
          if (!action && recommendedTools && recommendedTools.length > 0) {
              action = { type: 'RECOMMEND_TOOL', toolNames: recommendedTools };
          }

          if (followUpMatch?.[1]) {
              try {
                  followUpPrompts = JSON.parse(followUpMatch[1]);
                  finalText = finalText.replace(followUpMatch[0], '').trim();
              } catch (e) {
                  console.error("Failed to parse follow-up prompts", e);
              }
          }
          
          setSessions(prev => prev.map(s => {
              if (s.id === currentSessionId) {
                  const newMessages = s.messages.map(m => 
                      m.id === modelMessageId ? {
                          ...m,
                          text: finalText,
                          source,
                          action,
                          recommendedTools,
                          addDrugName,
                          followUpPrompts,
                          groundingMetadata
                      } : m
                  );
                  return { ...s, messages: newMessages };
              }
              return s;
          }));
          
          if (subscription?.plan === 'Trial' || subscription?.plan === 'Free') {
            decrementInteractions();
          }

      } catch (error: any) {
          console.error("Error communicating with AI service:", error);
          const errorMessage: Message = { id: (Date.now() + 1).toString(), text: "Unable to reach the clinical reasoning service. Please verify your network connection and retry.", role: ChatMessageRole.MODEL };
          setSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: s.messages.map(m => m.id === modelMessageId ? errorMessage : m) } : s));
      } finally {
          setIsLoading(false);
      }
  };

    const handleEditMessage = (messageId: string, newText: string, answerLength: AnswerLength) => {
        if (!activeSessionId) return;

        setSessions(prev => prev.map(s => {
            if (s.id === activeSessionId) {
                const messageIndex = s.messages.findIndex(m => m.id === messageId);
                if (messageIndex === -1) return s;
                const newMessages = s.messages.slice(0, messageIndex);
                return { ...s, messages: newMessages };
            }
            return s;
        }));
        
        setTimeout(() => {
            handleSendMessage(newText, answerLength);
        }, 0);
    };

  const handleRegenerate = () => {
    if (!activeSessionId || isLoading) return;
    const currentSession = sessions.find(s => s.id === activeSessionId);
    if (!currentSession || currentSession.messages.length === 0) return;

    // Find the last user message
    let lastUserIndex = -1;
    for (let i = currentSession.messages.length - 1; i >= 0; i--) {
      if (currentSession.messages[i].role === ChatMessageRole.USER) {
        lastUserIndex = i;
        break;
      }
    }
    if (lastUserIndex === -1) return;

    const lastUserMessage = currentSession.messages[lastUserIndex];
    handleEditMessage(lastUserMessage.id, lastUserMessage.text, settings.answerLength);
  };
  
  const handleSendMessageFromDashboard = (message: string, answerLength: AnswerLength) => {
    setCurrentView('chat');
    setInitialAnswerLength(answerLength);
    setInitialMessage(message);
    sentInitialMessage.current = false;
    setActiveSessionId(null);
  };

  const handleAddDrugSuccess = (newDrug?: DrugInfo) => {
    setDrugToAdd(null);
    if (newDrug) {
      showToast(`"${newDrug.brandName}" added to your Drug Index.`, 'success');
      const successMessage: Message = {
          id: Date.now().toString(),
          text: `"${newDrug.brandName}" has been added to your personal Drug Index.`,
          role: ChatMessageRole.MODEL,
          source: 'local'
      };
      setSessions(prev => prev.map(s => s.id === activeSessionId ? { ...s, messages: [...s.messages, successMessage] } : s));
    } else {
      showToast('Medication added to your Drug Index.', 'success');
    }
  };
  
  const handleNewSession = () => {
    setActiveSessionId(null);
    setInitialMessage(null);
    sentInitialMessage.current = false;
  };

  const handleDeleteSession = (id: string) => {
      setSessions(prev => prev.filter(s => s.id !== id));
      if (activeSessionId === id) {
          setActiveSessionId(null);
      }
      showToast('Consultation deleted.', 'info', 2000);
  };

  const handleRenameSession = (id: string, newTitle: string) => {
      setSessions(prev => prev.map(s => s.id === id ? { ...s, title: newTitle } : s));
  };

  const handleTogglePinSession = (id: string) => {
      setSessions(prev => prev.map(s => {
          if (s.id === id) {
              const nextPinned = !s.isPinned;
              showToast(nextPinned ? 'Consultation pinned to top.' : 'Consultation unpinned.', 'info', 2000);
              return { ...s, isPinned: nextPinned };
          }
          return s;
      }));
  };

  const handleClearHistory = async () => {
      const confirmed = await confirmAction({
          title: 'Clear All Clinical Consultations',
          message: 'Are you sure you want to delete all saved chat consultations? This action cannot be undone.',
          confirmLabel: 'Clear All History',
          variant: 'danger'
      });
      if (confirmed) {
          setSessions([]);
          setActiveSessionId(null);
          showToast('All consultation history cleared.', 'success');
      }
  };

  const handleNavigate = (view: View) => {
    if (view === 'chat') {
      setInitialMessage(null);
      sentInitialMessage.current = false;
    }
    if (view === 'evidence-search') setInitialQuery(null);
    setCurrentView(view);
  };

  const handleEvidenceSearchFromDashboard = (query: string) => {
    addActivity({
        type: 'evidence_search',
        text: `Searched evidence for "${query.substring(0, 30)}..."`,
        payload: { query }
    });
    setInitialQuery(query);
    setCurrentView('evidence-search');
  };

  const handleSelectDrug = async (drugId: string) => {
    if(!uid) return;
    const drug = await getDrugById(drugId, uid);
    if(drug) {
        addActivity({
            type: 'view_drug',
            text: `Viewed ${drug.brandName}`,
            payload: { drugId: drug.id }
        });
    }
    setSelectedDrugId(drugId);
    setCurrentView('drug-detail');
  };
  
  const handleStartQuiz = (settings: QuizSettings) => {
    setQuizSettings(settings);
    setCurrentView('quiz');
  };

  const handleEndQuiz = () => {
    setQuizSettings(null);
    setCurrentView('study-mode');
  };

  const handleClinicalGuideSelect = (topic: string, source: string) => {
    const message = `Provide a concise summary of the key recommendations from the latest ${source} guidelines for the management of ${topic}.`;
    setInitialMessage(message);
    setInitialAnswerLength('detailed');
    setCurrentView('chat');
  };

  const handleSetActiveTool = (toolName: string | null) => {
    if (toolName) {
        addActivity({
            type: 'use_tool',
            text: `Used ${toolName}`,
            payload: { toolName }
        });
    }
    setActiveTool(toolName);
  };
  
  const handleDashboardSearch = async (query: string) => {
      if (!uid) return;
      
      addActivity({
          type: 'start_chat',
          text: `Searched for "${query.substring(0, 30)}..."`,
          payload: { query }
      });

      const drug = await findDrugByName(query, uid);
      if (drug) {
          handleSelectDrug(drug.id);
      } else {
          handleSendMessageFromDashboard(query, 'short');
      }
  };
  
  const handleActivityClick = (activity: Activity) => {
    const { type, payload } = activity;
    switch (type) {
        case 'view_drug':
            if (payload.drugId) handleSelectDrug(payload.drugId);
            break;
        case 'use_tool':
            if (payload.toolName) handleSetActiveTool(payload.toolName);
            break;
        case 'start_chat':
            if (payload.sessionId) {
                handleNavigate('chat');
                setActiveSessionId(payload.sessionId);
            } else if (payload.query) {
                handleNavigate('chat');
                handleSendMessageFromDashboard(payload.query, 'short');
            }
            break;
        case 'evidence_search':
            if (payload.query) handleEvidenceSearchFromDashboard(payload.query);
            break;
        default:
            break;
    }
};

  const activeSession = sessions.find(s => s.id === activeSessionId);

  const userDrugs = useMemo(() => {
    if (!uid) return [];
    return getUserDrugs(uid);
  }, [uid, sessions]);

  const analytics = {
    totalChats: sessions.length,
    toolsUsed: new Set(recentActivities.filter(a => a.type === 'use_tool').map(a => a.payload.toolName)).size,
    customDrugs: userDrugs.length,
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <AnimatedLogo />
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }
  
  if (currentView === 'quiz' && quizSettings) {
    return <Quiz settings={quizSettings} onEndQuiz={handleEndQuiz} />;
  }
  
  if (currentView === 'admin-dashboard') {
    if (isAdmin) {
        return <AdminDashboard onNavigate={handleNavigate} />;
    } else {
        setTimeout(() => setCurrentView('dashboard'), 0);
        return null; 
    }
  }

  return (
    <>
      <div className="text-gray-200 min-h-screen-ios flex flex-col page-enter-active">
        {!isOnline && (
          <div className="bg-amber-950/90 border-b border-amber-500/40 text-amber-200 px-4 py-2 text-xs md:text-sm text-center font-medium flex items-center justify-center gap-2 sticky top-0 z-50 backdrop-blur-md shadow-md">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
            <span>Offline Mode Active: Local Drug Monograph Database &amp; Clinical Calculators Available Offline</span>
          </div>
        )}
        {currentView === 'chat' ? (
          <main className="flex-grow flex flex-col w-full max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 pt-4 pb-24">
            <Chat 
                messages={activeSession?.messages || []}
                onNavigate={handleNavigate}
                sessions={sessions}
                activeSessionId={activeSessionId}
                onSelectSession={setActiveSessionId}
                onNewChat={handleNewSession}
                onDeleteSession={handleDeleteSession}
                onRenameSession={handleRenameSession}
                onTogglePinSession={handleTogglePinSession}
                settings={settings}
                onSettingsChange={setSettings}
                onClearAllSessions={handleClearHistory}
                isLoading={isLoading}
                onSendMessage={handleSendMessage}
                onRegenerate={handleRegenerate}
                onDrugAdded={() => handleAddDrugSuccess()}
                setActiveTool={handleSetActiveTool}
                onSelectDrug={handleSelectDrug}
            />
          </main>
        ) : (
          <main className={`flex-grow flex flex-col w-full mx-auto px-4 sm:px-6 lg:px-8 ${currentView === 'dashboard' ? 'pb-40' : 'pb-36'} ${currentView === 'account' || currentView === 'subscription-management' ? 'max-w-4xl' : 'max-w-7xl'}`}>
              {currentView === 'dashboard' && (
                  <Dashboard 
                      onSendMessageFromDashboard={handleSendMessageFromDashboard}
                      onDashboardSearch={handleDashboardSearch}
                      onEvidenceSearch={handleEvidenceSearchFromDashboard}
                      onSelectDrug={handleSelectDrug}
                      isVoiceEnabled={isVoiceEnabled}
                      isPushEnabled={isPushEnabled}
                      isPwaInstallable={isInstallable}
                      isPwaInstalled={isInstalled}
                      onPwaInstall={promptInstall}
                      onSignUp={loginWithGoogle}
                      platform={platform}
                      quickAccessTools={quickAccessTools}
                      onOpenEditQuickAccess={() => setIsEditQuickAccessOpen(true)}
                      setActiveTool={handleSetActiveTool}
                      recentActivities={recentActivities}
                      onClearActivity={handleClearActivity}
                      sessions={sessions}
                      onNavigate={handleNavigate}
                      onSelectSession={setActiveSessionId}
                      analytics={analytics}
                      onActivityClick={handleActivityClick}
                  />
              )}
              {currentView === 'library' && (
                <Tools 
                    onNavigate={handleNavigate} 
                    onSelectDrug={handleSelectDrug}
                    setActiveTool={handleSetActiveTool}
                    onSelectGuide={handleClinicalGuideSelect}
                    uid={uid}
                />
              )}
              {currentView === 'all-tools' && (
                <AllTools 
                  quickAccessTools={quickAccessTools} 
                  setActiveTool={handleSetActiveTool}
                  onOpenEditQuickAccess={() => setIsEditQuickAccessOpen(true)}
                  onNavigate={handleNavigate}
                  uid={uid}
                  onSelectDrug={handleSelectDrug}
                  onSelectGuide={handleClinicalGuideSelect}
                />
              )}
              {currentView === 'account' && (
                  <Account 
                      onNavigate={handleNavigate}
                      onOpenInfoModal={handleOpenInfoModal}
                      isVoiceEnabled={isVoiceEnabled} 
                      onVoiceEnabledChange={setIsVoiceEnabled} 
                      isPushEnabled={isPushEnabled} 
                      onPushEnabledChange={setIsPushEnabled}
                      isSentryEnabled={isSentryEnabled}
                      onSentryEnabledChange={setIsSentryEnabled}
                      isPwaInstallable={isInstallable}
                      isPwaInstalled={isInstalled}
                      onPwaInstall={promptInstall}
                      platform={platform}
                      onStartTour={() => setShowTour(true)}
                  />
              )}
              {currentView === 'evidence-search' && <EvidenceSearch initialQuery={initialQuery} onNavigate={handleNavigate} />}
              {currentView === 'drug-index' && <DrugIndex onNavigate={handleNavigate} onSelectDrug={handleSelectDrug} />}
              {currentView === 'drug-detail' && selectedDrugId && <DrugDetail drugId={selectedDrugId} onNavigate={handleNavigate} onSendMessage={handleSendMessage} setActiveTool={setActiveTool} />}
              {currentView === 'study-mode' && <StudyMode onNavigate={handleNavigate} onStartQuiz={handleStartQuiz} />}
              {currentView === 'clinical-guides' && <ClinicalGuides onNavigate={handleNavigate} onSelectTopic={handleClinicalGuideSelect} />}
              {currentView === 'subscription-management' && <SubscriptionManagement onNavigate={handleNavigate} />}
              {currentView === 'pricing' && <Pricing onNavigate={handleNavigate} onOpenInfoModal={handleOpenInfoModal} />}
          </main>
        )}
        <Footer currentView={currentView} onNavigate={handleNavigate} />
      </div>
      
      {drugToAdd && <AddDrugModal drugName={drugToAdd} onClose={() => setDrugToAdd(null)} onSave={handleAddDrugSuccess} />}
      {showIosInstallModal && <InstallInstructionsModal onClose={() => setShowIosInstallModal(false)} />}
      {isIdle && <InactivityModal countdown={countdown} onStay={resetTimer} onLogout={logout} />}
      {isUpgradeModalOpen && <UpgradeModal onNavigate={handleNavigate} onClose={() => setIsUpgradeModalOpen(false)} />}
      {isEditQuickAccessOpen && (
        <EditQuickAccessModal 
            isOpen={isEditQuickAccessOpen}
            onClose={() => setIsEditQuickAccessOpen(false)}
            currentTools={quickAccessTools}
            onSave={(newTools) => {
                setQuickAccessTools(newTools);
                setIsEditQuickAccessOpen(false);
            }}
        />
      )}
      <ToolRenderer activeTool={activeTool} onClose={() => setActiveTool(null)} />
      {infoModalContent && <InfoModal title={infoModalContent.title} content={infoModalContent.content} onClose={() => setInfoModalContent(null)} />}
      {showTour && <OnboardingTour onComplete={handleCompleteTour} />}
    </>
  );
};

export default App;